import { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { TbPaperclip } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AttachmentDestination, CloudAttachment, CloudStorageConnection, getCloudAttachmentContent } from '@/services';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import LoadingContent from '@/shared/components/layouts/views/LoadingContent';

import { DocumentConceptReference, DocumentIntelligence } from '../AttachmentRedactionEditor/documentIntelligence';
import { ExtractedDocument } from '../documentExtraction';
import DocxAttachmentPreview from '../DocxAttachmentPreview';

export interface AttachmentDocumentReview {
    activeReferenceId?: string;
    document?: ExtractedDocument;
    intelligence: DocumentIntelligence;
    loading: boolean;
    textExtractionSupported: boolean;
    onReferenceSelect: (reference: DocumentConceptReference) => void;
}

interface AttachmentPreviewProps {
    attachment: CloudAttachment;
    bordered?: boolean;
    connection: CloudStorageConnection;
    destination?: AttachmentDestination;
    onReviewChange?: (review?: AttachmentDocumentReview) => void;
    sourcePage?: number;
}

export const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const PREVIEWABLE_MIME_TYPES = new Set([
    'application/json',
    'application/pdf',
    'image/gif',
    'image/jpeg',
    'image/png',
    'image/webp',
    'text/csv',
    'text/plain',
]);

const AttachmentPreview: FC<AttachmentPreviewProps> = ({
    attachment,
    bordered = true,
    connection,
    destination,
    onReviewChange,
    sourcePage,
}) => {
    const { token } = useAntDesignTheme();
    const documentInputRef = useRef({ attachment, connection, destination });
    const [preview, setPreview] = useState<{
        contentType?: string;
        error?: string;
        loading: boolean;
        url?: string;
    }>({ loading: true });

    useEffect(() => {
        documentInputRef.current = { attachment, connection, destination };
    }, [attachment, connection, destination]);

    useEffect(() => {
        if (attachment.mimeType === DOCX_MIME_TYPE) {
            return;
        }

        const controller = new AbortController();
        const documentInput = documentInputRef.current;
        let objectUrl: string | undefined;

        void getCloudAttachmentContent({ ...documentInput, signal: controller.signal })
            .then((content) => {
                if (controller.signal.aborted) {
                    return;
                }

                const contentType = content.type || attachment.mimeType;

                if (!PREVIEWABLE_MIME_TYPES.has(contentType)) {
                    setPreview({ error: 'This file type cannot be safely previewed in the browser.', loading: false });
                    return;
                }

                objectUrl = URL.createObjectURL(content);
                setPreview({ contentType, loading: false, url: objectUrl });
            })
            .catch((error: unknown) => {
                if (!controller.signal.aborted) {
                    captureException(error);
                    setPreview({
                        error: 'The attachment could not be loaded from the connected storage provider.',
                        loading: false,
                    });
                }
            });

        return () => {
            controller.abort();
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [
        attachment.id,
        attachment.mimeType,
        connection.id,
        destination?.listName,
        destination?.recordName,
        destination?.resourceId,
        destination?.resourceName,
        destination?.resourceType,
    ]);

    if (attachment.mimeType === DOCX_MIME_TYPE) {
        return (
            <DocxAttachmentPreview
                attachment={attachment}
                connection={connection}
                destination={destination}
                onReviewChange={onReviewChange}
            />
        );
    }

    if (preview.loading) {
        return <LoadingContent />;
    }

    if (!preview.url) {
        return (
            <EmptyView
                description={preview.error ?? 'The file could not be previewed.'}
                height='100%'
                icon={<TbPaperclip style={{ color: token.colorTextTertiary }} />}
                label='Preview Unavailable'
            />
        );
    }

    const previewStyle: CSSProperties = {
        backgroundColor: token.colorBgContainer,
        border: bordered ? `1px solid ${token.colorBorder}` : 0,
        borderRadius: bordered ? token.borderRadiusLG : 0,
        height: '100%',
        width: '100%',
    };

    if (preview.contentType?.startsWith('image/')) {
        return <img alt={attachment.name} src={preview.url} style={{ ...previewStyle, objectFit: 'contain' }} />;
    }

    return (
        <iframe
            allowFullScreen
            referrerPolicy='no-referrer'
            sandbox={preview.contentType === 'application/pdf' ? undefined : 'allow-same-origin'}
            src={
                preview.contentType === 'application/pdf' && sourcePage
                    ? `${preview.url}#page=${sourcePage}`
                    : preview.url
            }
            title={attachment.name}
            style={previewStyle}
        />
    );
};

export type { AttachmentPreviewProps };
export default AttachmentPreview;
